import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import { useLocations } from "@/hooks/use-api";
import { ListPage } from "@/components/v2/list-page";
import { DataTable } from "@/components/v2/data-table";

type Row = Record<string, unknown>;

const columns: ColumnDef<Row, unknown>[] = [
  {
    accessorKey: "formatted_address",
    header: "Address",
    cell: ({ row }) => {
      const address =
        (row.original.formatted_address as string | undefined) ||
        (row.original.street_line_1 as string | undefined);
      return (
        <span className="font-medium transition-colors group-hover:text-[color:var(--color-accent)]">
          {address || "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "city",
    header: "City",
    cell: ({ row }) => {
      const city = row.original.city as string | undefined;
      return (
        <span className={city ? "" : "text-[color:var(--color-text-lo)]"}>
          {city || "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "state",
    header: "State",
    cell: ({ row }) => {
      const state = row.original.state as string | undefined;
      return (
        <span className={state ? "" : "text-[color:var(--color-text-lo)]"}>
          {state || "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "postal_code",
    header: "Zip",
    enableSorting: false,
    cell: ({ row }) => {
      const zip = row.original.postal_code as string | undefined;
      return (
        <span
          className={zip ? "typo-mono" : "text-[color:var(--color-text-lo)]"}
        >
          {zip || "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "location_type",
    header: "Type",
    enableSorting: false,
    cell: ({ row }) => {
      const type = row.original.location_type as string | undefined;
      return (
        <span className={type ? "" : "text-[color:var(--color-text-lo)]"}>
          {type || "—"}
        </span>
      );
    },
  },
  {
    id: "geocoded",
    header: "Geocoded",
    enableSorting: false,
    cell: ({ row }) => {
      const geocoded =
        row.original.latitude != null && row.original.longitude != null;
      return (
        <span
          className={
            geocoded
              ? "text-[color:var(--color-success)]"
              : "text-[color:var(--color-text-lo)]"
          }
        >
          {geocoded ? "Geocoded" : "Not geocoded"}
        </span>
      );
    },
  },
];

export default function V2LocationsPage() {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([
    { id: "created_at", desc: true },
  ]);
  const [pageIndex, setPageIndex] = useState(0);
  const pageSize = 50;

  const { data, isLoading } = useLocations({
    offset: pageIndex * pageSize,
    limit: pageSize,
    sort_by: sorting[0]?.id,
    sort_order: sorting[0]?.desc ? "desc" : "asc",
  });

  const locations = (data?.locations ?? []) as Row[];
  const total = data?.total ?? 0;

  return (
    <ListPage
      title="Locations"
      subtitle={`${total.toLocaleString()} total locations`}
    >
      <DataTable
        columns={columns}
        data={locations}
        total={total}
        isLoading={isLoading}
        onRowClick={(row) =>
          navigate(`/locations/${row.id}`, {
            state: { ids: locations.map((r) => String(r.id)) },
          })
        }
        emptyMessage="No locations yet"
        emptyDescription="Locations are added automatically from customer addresses."
        pageSize={pageSize}
        pageIndex={pageIndex}
        onPaginationChange={setPageIndex}
        sorting={sorting}
        onSortingChange={setSorting}
      />
    </ListPage>
  );
}
